import {render} from 'preact';
import $ from '@modules/jquery-import';
import appLogger from '@core/LoggingProvider';
import {jqueryValidate} from '@modules/lazy-imports';
import CustomerService from '@services/Customer';
import ResourcesService from '@services/ResourcesService';
import Notifier from '@modules/ui/Notifier';
import dataLayer from '@upi/data-layer';

const LABEL_TERRITORY_ACCOUNT_MANAGER = upi.i18n.getTextFromKey(
    'lbl-territoryAccountManager',
    'Territory Account Manager'
);

const LABEL_TAM_SEARCH_ERROR = upi.i18n.getTextFromKey(
    'lbl-tamSearchError',
    'Sorry, there was a problem while searching for your Territory Account Manager. Please try again.' )

const LABEL_USER_FEEDBACK_ERROR = upi.i18n.getTextFromKey(
    'lbl-userFeedbackError',
    'Sorry, there was a problem while attempting to submit your feedback. Please try again.' );

const LABEL_TAM_SEARCH_NO_RESULTS = upi.i18n.getTextFromKey(
    'lbl-tamSearchNoResults',
    `No TAM was found at this ZIP code, please enter another ZIP code, or contact customer support at ${upi.conf.phoneTollFree} or another method listed above`
);

document.addEventListener( 'DOMContentLoaded', () => {
    const feedback = document.getElementById( 'customer-feedback-section' );
    const tamSearch = document.getElementById( 'tam-search' );

    if ( feedback ) {
        new FeedbackForm( feedback );
    }

    if ( tamSearch ) {
        new TamSearch( tamSearch );
    }
} );

class FeedbackForm {
    constructor ( container ) {
        this.feedback = container.querySelector( '.feedback-form' );
        this.successMessage = container.querySelector( '.feedback-success' );
        this.submitBtn = this.feedback.querySelector( '.button[type="submit"]' );
        this.submitMoreBtn = this.successMessage.querySelector( '.button' );
        this.form = this.feedback.querySelector( 'form' );

        jqueryValidate().then( () => this.initEvents() );
    }

    initEvents () {
        this.validator = $( this.form ).validate( {
            submitHandler: ( form ) => {
                this.handleEmailSubmission( form );
                return false;
            }
        } );

        this.submitMoreBtn.addEventListener( 'click', () => this.toggleSuccessMessage( false ) );
    }

    handleEmailSubmission ( form ) {
        const formDataSanitized = {};
        this.submitBtn.classList.add( 'processing' );

        const formData = new FormData( form );
        for ( const pair of formData.entries() ) {
            formDataSanitized[pair[0]] = pair[1].trim();
        }

        CustomerService.submitUserFeedback( formDataSanitized )
            .then( () => {
                this.toggleSuccessMessage( true );

                dataLayer.forms.formSubmit( {
                    formName: 'Contact Us Feedback'
                } );

                form.reset();
                this.validator.resetForm();
            } )
            .catch( err => {
                appLogger.capture( err );
                Notifier.error( LABEL_USER_FEEDBACK_ERROR );
            } )
            .finally( () => {
                this.submitBtn.classList.remove( 'processing' );
            } )

    }

    toggleSuccessMessage ( showSuccess ) {
        this.feedback.classList.toggle( 'hidden', showSuccess );
        this.successMessage.classList.toggle( 'hidden', !showSuccess );
    }
}

class TamSearch {
    constructor ( container ) {
        this.container = container;
        this.form = this.container.querySelector( '.tam-search-form' );
        this.submitBtn = this.form.querySelector( '.button[type="submit"]' );

        jqueryValidate().then( () => {
            $.validator.addClassRules( 'zipcode', {
                required: true,
                digits: true,
                minlength: 5,
                maxlength: 5
            } );
            this.initEvents();
        } );
    }

    initEvents () {
        this.validator = $( this.form ).validate( {
            submitHandler: ( form ) => {
                this.handleSearch( form );
                return false;
            }
        } );
    }

    handleSearch ( form ) {
        const formDataSanitized = {};
        this.submitBtn.classList.add( 'processing' );

        const formData = new FormData( form );
        for ( const pair of formData.entries() ) {
            formDataSanitized[pair[0]] = pair[1].trim();
        }

        ResourcesService.getTamsByZipCode( formDataSanitized.ZipCode )
            .then( ( results ) => {
                if ( results?.length <= 0 ) {
                    this.validator.showErrors( {
                        'ZipCode': LABEL_TAM_SEARCH_NO_RESULTS
                    } );
                    return;
                }
                this.renderResults( results );
                form.reset();
                this.validator.resetForm();
            } )
            .catch( err => {
                console.error( err );
                appLogger.capture( err );
                Notifier.error( LABEL_TAM_SEARCH_ERROR );
            } )
            .finally( () => {
                this.submitBtn.classList.remove( 'processing' );
            } )
    }

    renderResults ( results ) {
        if ( results && results.length > 0 ) {
            const mountTo = document.createElement( 'div' );
            this.container.insertAdjacentElement( 'beforebegin', mountTo );
            this.container.remove();
            render(
                <div>
                    {results.map( props => <TamDetail key={props.email} {...props}/> )}
                </div>,
                mountTo
            );
        }
    }
}

const TamDetail = ( {firstName, lastName, email, phone, territory, image} ) =>
    <div class="contact-card relative rounded-lg shadow-diffuse bg-white mb-8 md:mb-12 md:mr-12">
        <div class="contact-card--inner">
            <div class="contact-card--figure-container">
                <figure class="contact-card--figure-portrait rounded-full">
                    {image.source != null
                        ?
                        <img className="absolute pin-t"
                             alt={`${firstName} ${lastName} - ${LABEL_TERRITORY_ACCOUNT_MANAGER}`}
                             src={image.source}/>
                        :
                        <svg width="147" height="147" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="73.5" cy="73.5" r="73.5" fill="#fff"/>
                            <mask id="a" maskUnits="userSpaceOnUse" style="mask-type:alpha"
                                  x="0" y="0" width="147" height="147">
                                <circle cx="73.5" cy="73.5" r="73.5" fill="#D9D9D9"/>
                            </mask>
                            <g mask="url(#a)">
                                <path fill="#63666A"
                                      d="M135.28 133.29c-.47 4.28-.97 8.53-1.47 12.81l-145.11 3.6c.03-.23.1-.5.12-.73.6-3.15.7-6.45 3.29-8.99.35-.34.33-1.04.5-1.58.9-2.69 1.69-5.4 2.7-8.07 1.04-2.7 2.37-5.33 3.41-8.02.34-.91.27-1.94.25-2.94-.09-3.47 1.48-6.48 3.2-9.39a4.62 4.62 0 0 1 1.97-1.65c1.87-.81 3.86-1.43 5.81-2.18 5.43-2.14 10.85-4.3 16.24-6.44.21-.07.35-.24.5-.35.02-.13.1-.2.06-.26-.2-.63-.36-1.3-.55-1.92-1.26-4.48-.04-8.5 2.92-12.05 1.8-2.15 3.98-3.97 6.08-5.89 1.93-1.78 3.63-3.73 3.46-6.32-.28-4.3-.71-8.6-1.32-12.88-.56-3.92-1.27-7.8-.36-11.73a104.56 104.56 0 0 1 6.2-18.57c2.87-6.57 6.15-12.82 11.46-18.13 5.87-5.85 12.52-9.05 21.3-7.26.69.15 1.5-.2 2.25-.33 1.32-.23 2.7-.76 4-.66 4.26.32 7.85 2.14 10.71 5.03a77.69 77.69 0 0 1 5.75 6.57c5.29 6.7 9.83 13.76 13.14 21.48 2.05 4.76 2.02 9.6.32 14.47-1.14 3.27-2.2 6.53-1.58 10.08.42 2.4.27 4.86.69 7.26.66 3.75 3.52 6.45 5.57 9.56 1.19 1.8 2.02 3.66 1.86 5.8-.13 2.03-.47 4.07-.67 6.11-.17 1.94-.16 3.88-.4 5.78-.33 2.55.31 3.6 2.98 4.27 1.19.3 2.48.4 3.63.77 1.59.53 3.64.91 4.6 2.02a23.81 23.81 0 0 1 4.44 7.27c1.15 3.27 1.5 6.83 2.15 10.28.2 1.1.45 2.2.4 3.3-.06 3.27-.12 6.6-.5 9.88ZM31.72 88.83c-2.13 2.36-2.93 4.75-2.36 7.47.12.5.67.95 1 1.4.31-.47.84-.88.9-1.38.34-2.25.6-4.49.83-6.73.04-.17-.18-.36-.37-.76Z"/>
                            </g>
                        </svg>
                    }
                </figure>
            </div>
            <div class="leading-normal w-full mx-8">
                <h4 class="headline-xs">{firstName} {lastName}</h4>
                <p class="mb-2 italic">{LABEL_TERRITORY_ACCOUNT_MANAGER}</p>
                {territory &&
                    <p class="mb-4">{territory}</p>
                }
                <ul class="no-pad leading-loose">
                    {phone &&
                        <li>
                            <span class="flex items-center">
                                <svg className="svgi-phone w-6 h-6 mr-2">
                                    <use xlinkHref="/svg/ui-defs.svg#icon-phone"></use>
                                </svg>
                                {phone}
                            </span>
                        </li>
                    }
                    {email &&
                        <li>
                            <a className="flex items-center" href={`mailto:${email}`}>
                                <svg className="svgi-email w-6 h-6 mr-2">
                                    <use xlinkHref="/svg/ui-defs.svg#icon-email"></use>
                                </svg>
                                {email}
                            </a>
                        </li>
                    }
                </ul>
            </div>
        </div>
    </div>
